import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {WarningCircle} from '../../icons';
import {ClientPanelApi} from '../../../api/clientPanelApi';
import {AccessService} from '../../../utils/AccessService';
import {useFicha21} from '../../../context/UserModulesContext';
import {anchorAttr} from '../../../guides/anchors';
import {ClientTabs} from '../ClientTabs';
import {PanelHeader} from './PanelHeader';
import {FirstStepsStrip} from './FirstStepsStrip';
import {KpiStrip} from './KpiStrip';
import {ControlLinesCard} from './ControlLinesCard';
import {EconomyCard} from './EconomyCard';
import {MemoryCard} from './MemoryCard';
import {PlatformsCard} from './PlatformsCard';
import {TeamCard} from './TeamCard';
import {NewLinePathModal} from '../../onboarding/path_zero/NewLinePathModal';
import {lineWizardEntryPath} from '../../line_wizard/lineWizardDraft';
import {IaCard} from './IaCard';
import {BitacoraCard} from './BitacoraCard';
import {PortalCard} from './PortalCard';
import {ConversionModal} from './ConversionModal';
import {LineGoalModal} from './LineGoalModal';
import {LineManageConfirmModal, useLineManageConfirm} from './useLineManageConfirm';
import './PanelV11.scss';

/**
 * Panel del cliente V11 (UX 2.1) — el shell de la pantalla nueva.
 *
 * Toda la pantalla se alimenta de UN agregado (`GET client/<id>/panel/`): las cards no hacen fetch
 * propio, reciben su bloque del payload y una función para refrescarlo. Cada bloque puede venir
 * ausente (capability apagada para ESE cliente) — la card se cae sola, sin romper el resto.
 *
 * Esta etapa (F2) trae el shell + la bifurcación; las cards son stubs que rellenan otras tandas.
 */
export function PanelV11Content({clientId, history}) {
    const {t} = useTranslation();
    const {mediaPlanEnabled, clientMemoryLevel, modules, clientEconomicsEnabled} = useFicha21();

    const [panel, setPanel] = useState(null);
    const [panelFailed, setPanelFailed] = useState(false);
    const [loading, setLoading] = useState(true);
    // Contador que dispara de nuevo el fetch: es lo que usan el botón "Reintentar" y las cards
    // cuando mutan algo (conectar un media plan, ocultar los primeros pasos, etc.).
    const [reloadToken, setReloadToken] = useState(0);

    const refetchPanel = useCallback(() => setReloadToken(token => token + 1), []);

    // Editar una línea desde el Panel lleva a la edición COMPLETA (`/business-unit/:id/`, con sus
    // solapas). Hasta el 24/09 abría un drawer lateral de edición rápida (`LineEditDrawer`); fede lo
    // sacó del Panel en la card bBcBPqGg («ese side panel por ahora no lo usemos»). La vista de
    // todas las líneas (`LinesView`) lo sigue usando.
    const handleEditLine = useCallback((lineId) => {
        history.push(`/business-unit/${lineId}/`);
    }, [history]);
    // «Definir objetivo» abre SÓLO el modal de objetivo (card 6zUFUONB).
    const [goalLineId, setGoalLineId] = useState(null);
    const handleDefineGoal = useCallback((lineId) => setGoalLineId(lineId), []);
    const closeGoalModal = useCallback(() => setGoalLineId(null), []);
    // «Desactivar», al pie del detalle de la fila. La card no escribe: avisa, y acá se confirma y
    // se llama a `lineManageActions` — un solo camino de escritura (invariante de la card IysOiDWB).
    const lineManage = useLineManageConfirm(refetchPanel);

    // El chip de conversión del header abre el modal que explica la regla (la conversión se define
    // por cuenta) y manda a editarla cuenta por cuenta con el flujo existente
    // (`POST client/ad_account/<id>/selected_actions`). No hay endpoint a nivel cliente.
    const [conversionOpen, setConversionOpen] = useState(false);
    const handleEditConversion = useCallback(() => setConversionOpen(true), []);
    const closeConversion = useCallback(() => setConversionOpen(false), []);
    // "Nueva línea de control" pasa por el paso 0 (desde cuentas / paso a paso / media plan), igual
    // que la vista de líneas: antes este botón abría directo el modal viejo y el de la vista pasaba por
    // el paso 0, así que el mismo rótulo hacía dos cosas distintas (card CvdQbnl2). «Paso a paso» (ex
    // «A mano») es el wizard. Lo usan también la franja de primeros pasos, el KPI «Sin presupuesto» y el
    // estado vacío de la card de líneas.
    const [showPathZero, setShowPathZero] = useState(false);
    const handleNewLine = useCallback(() => setShowPathZero(true), []);
    const openLineWizard = useCallback(() => {
        history.push(lineWizardEntryPath(clientId));
    }, [history, clientId]);

    // Cambiar de cliente NO remonta esta pantalla: la ruta `/client/:id` se reusa (el selector del
    // topbar navega de `/client/5` a `/client/7`) y React reconcilia el mismo elemento con un
    // `clientId` nuevo. Sin borrar el payload viejo, el header y los primeros pasos siguen
    // mostrando al cliente que se acaba de dejar mientras llega el agregado — con el id nuevo ya
    // cableado en los modales. Va aparte del fetch a propósito: `reloadToken` (refrescar después de
    // conectar una cuenta, ocultar los pasos, etc.) NO tiene que vaciar la pantalla. El modal de
    // objetivo se cierra con el cambio: si no, quedaría abierto sobre una línea del cliente anterior.
    useEffect(() => {
        closeGoalModal();
        setPanel(null);
        setPanelFailed(false);
    }, [clientId, closeGoalModal]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);

        // El `tz_offset` (y el encoding del id) los arma `ClientPanelApi`: la URL del agregado se
        // escribe en un solo lugar, igual que la de ocultar/mostrar los primeros pasos.
        ClientPanelApi.getPanel(clientId).then((data) => {
            if (cancelled) return;
            if (data && data.status === 'OK' && data.data) {
                setPanel(data.data);
                setPanelFailed(false);
            } else {
                // 404 uniforme (cliente ajeno / tier < UX_21) o error de red: no distinguimos, el
                // Panel no puede filtrar la existencia de nada (aislamiento cross-agency).
                setPanel(null);
                setPanelFailed(true);
            }
            setLoading(false);
        });

        return () => {
            cancelled = true;
        };
    }, [clientId, reloadToken]);

    // Gates de las cards. Dos capas: la capability de la agencia (viene del provider, es la misma
    // que usa la ficha vieja) y el bloque del payload (si el backend no lo mandó, para ESTE cliente
    // no aplica). Mientras el payload no llegó se monta igual, en estado de carga.
    const hasMcp = (modules || []).includes(AccessService.MODULE_DASHBO_MCP);
    const hasMemoryLevel = clientMemoryLevel === AccessService.MEMORY_LEVEL_MEDIO
        || clientMemoryLevel === AccessService.MEMORY_LEVEL_TOTAL;

    const blockEnabled = (block) => !panel || (!!block && block.enabled !== false);
    const showMediaPlan = mediaPlanEnabled === true && hasMcp && blockEnabled(panel && panel.media_plan);
    const showMemory = hasMemoryLevel && blockEnabled(panel && panel.memory);
    // La sección Usuarios no tiene capability de agencia: existe para todos. El gate es sólo la
    // presencia del bloque, que además cubre la ventana en que el backend todavía no lo manda.
    const showTeam = blockEnabled(panel && panel.team);
    // Economía del cliente: módulo de suscripción (apagado por defecto) + el bloque del payload.
    // `economy` también alimenta el chip «Sugerido desde el margen» de cada fila y el modal de
    // objetivo; sin el módulo ninguno de los tres lo recibe.
    const showEconomy = clientEconomicsEnabled === true && blockEnabled(panel && panel.economy);
    const economy = showEconomy && panel ? (panel.economy || null) : null;

    const panelLines = (panel && panel.lines) || [];
    const goalLine = goalLineId === null
        ? null : (panelLines.find((line) => line.id === goalLineId) || null);

    const renderMain = () => {
        if (panelFailed) {
            // El agregado se cayó: empty-state en la columna principal, sin tumbar los tabs ni el
            // rail (que no dependen del payload). Nada de alert().
            return (
                <div className="pnl-error">
                    <WarningCircle size={34}/>
                    <h3>{t('client_panel.error_title', 'No pudimos cargar el panel')}</h3>
                    <p>{t('client_panel.error_text', 'Hubo un problema al traer los datos de este cliente. Intenta de nuevo en unos segundos.')}</p>
                    <button type="button" className="pnl-btn pnl-btn--primary" onClick={refetchPanel}>
                        {t('client_panel.error_retry', 'Reintentar')}
                    </button>
                </div>
            );
        }

        return (
            <>
                <ControlLinesCard
                    data={panel && panel.lines}
                    loading={loading}
                    clientId={clientId}
                    onRefetch={refetchPanel}
                    onNewLine={handleNewLine}
                    isNewClient={!!(panel && panel.is_new_client)}
                    latestAlert={panel && panel.kpis && panel.kpis.active_alerts && panel.kpis.active_alerts.latest}
                    // La leyenda del pie ("día 18 de 31") sale del mismo `kpis.month` que el KPI de
                    // inversión: dos fuentes para el mismo "día X de Y" se contradicen en pantalla.
                    month={panel && panel.kpis && panel.kpis.month}
                    // El media plan es otra vía de CREAR líneas, así que vive en esta card: el botón
                    // para vincularlo y, ya vinculado, la franja de la planilla (card gW32VovP). Sin
                    // la capability el destino es un 404. La franja compara el plan contra la
                    // proyección del cliente (`kpis.projection`), sin recalcular nada.
                    mediaPlanEnabled={showMediaPlan}
                    mediaPlan={panel && panel.media_plan}
                    projection={panel && panel.kpis && panel.kpis.projection}
                    currency={panel && panel.client && panel.client.currency}
                    onDefineGoal={handleDefineGoal}
                    onDeactivateLine={lineManage.requestDeactivate}
                    // Las desactivadas viajan aparte (`inactive_lines`): la card no las dibuja, sólo
                    // ofrece el camino a ellas (card XLWKaxjN).
                    inactiveCount={((panel && panel.inactive_lines) || []).length}
                    economy={economy}
                />

                {/* Economía del negocio: debajo de las líneas, que son las que la usan (el objetivo
                    sugerido de cada una sale de acá). `key` = el cliente, como el resto de las cards
                    con estado propio. */}
                {showEconomy && (
                    <EconomyCard
                        key={clientId}
                        data={panel && panel.economy}
                        loading={loading}
                        clientId={clientId}
                        clientName={panel && panel.client && panel.client.name}
                        onRefetch={refetchPanel}
                    />
                )}

                {showMemory && (
                    <MemoryCard
                        data={panel && panel.memory}
                        loading={loading}
                        clientId={clientId}
                        onRefetch={refetchPanel}
                    />
                )}

                {/* El bloque `panel.platforms` viene con los MISMOS shapes que `GET client/:id`:
                    `PlatformsCard` es un envoltorio del `PlatformsBox` de la ficha clásica, sin
                    adaptador y con sus handlers (conectar / ignorar) adentro. */}
                <PlatformsCard
                    data={panel && panel.platforms}
                    loading={loading}
                    clientId={clientId}
                    clientName={(panel && panel.client && panel.client.name) || ''}
                    onRefetch={refetchPanel}
                />

                {/* Quién llega a este cliente — el equipo de la agencia y los usuarios del portal.
                    Va al final de la columna: es información sobre el cliente, no sobre su
                    operación diaria. Sin capability propia: el único gate es que el bloque venga
                    (el backend decide qué mostrar según el rol de quien pregunta). */}
                {showTeam && (
                    <TeamCard
                        data={panel && panel.team}
                        portal={panel && panel.portal}
                        loading={loading}
                        clientId={clientId}
                    />
                )}
            </>
        );
    };

    return (
        <section className="section-content">
            <ClientTabs clientId={clientId}/>

            <div className="pnl-screen">
                <PanelHeader
                    panel={panel}
                    clientId={clientId}
                    onEditConversion={handleEditConversion}
                    onNewLine={handleNewLine}
                    newLineGuideAnchor={anchorAttr('panel_new_line')}
                />

                {/* La franja se dibuja sólo si el backend dice `visible: true` (quedan pendientes y
                    nadie la ocultó): mientras el agregado no llegó, `data` es null y no ocupa lugar. */}
                {/* `key` = el cliente: "ocultar" es una decisión POR CLIENTE y vive en el estado
                    local de la franja. Sin remontarla, ocultarla en un cliente la esconde también
                    en el siguiente (la pantalla no remonta al cambiar de `clientId`). */}
                <FirstStepsStrip
                    key={clientId}
                    data={panel && panel.first_steps}
                    clientId={clientId}
                    clientName={panel && panel.client && panel.client.name}
                    onNewLine={handleNewLine}
                />

                <KpiStrip
                    data={panel && panel.kpis}
                    loading={loading}
                    clientId={clientId}
                    currency={panel && panel.client && panel.client.currency}
                    isNewClient={!!(panel && panel.is_new_client)}
                    // El tile de objetivos se arma sumando `lines[].goal`: el backend ya manda el
                    // objetivo de cada línea y pedirle un total aparte sería una segunda fuente
                    // para el mismo número.
                    lines={panel && panel.lines}
                    // "Sin presupuesto" ofrece crear la primera línea: el mismo modal que el botón
                    // del header, no un flujo nuevo.
                    onNewLine={handleNewLine}
                />

                <div className="pnl-grid">
                    <div className="pnl-col-main">
                        {renderMain()}
                    </div>

                    <div className="pnl-col-rail">
                        {/* Sin módulo MCP la card de IA no se muestra (mismo gate que la ficha
                            clásica): nada de upsell en el rail en V1 del Panel. */}
                        {/* `key` = el cliente, como la franja de arriba: el prompt desplegado y la
                            biblioteca de fallback son estado local, y los ids de prompt salen del
                            catálogo (se repiten entre clientes). Sin remontar, el cliente siguiente
                            heredaba el prompt abierto y, hasta que llegaba su biblioteca, la del
                            anterior. */}
                        {hasMcp && <IaCard
                            key={clientId}
                            data={panel && panel.ai}
                            loading={loading}
                            clientId={clientId}
                            // Sólo lo usa el prompt de fallback, que llega crudo de la biblioteca y
                            // hay que completarle el `{cliente}` antes de mostrarlo/copiarlo.
                            clientName={panel && panel.client && panel.client.name}
                            onRefetch={refetchPanel}
                            isNewClient={!!(panel && panel.is_new_client)}
                        />}
                        {/* La Bitácora del rail NO sale del agregado: tiene su propio endpoint
                            (`GET client/<id>/activity-log/?page_size=4&categories=…`), así que la
                            card sobrevive a un fallo del panel. */}
                        {/* `businessUnits` es sólo para el selector del modal "Agregar nota": si el
                            agregado se cayó llega vacío y la nota se guarda igual, sin línea. */}
                        <BitacoraCard clientId={clientId} history={history}
                                      businessUnits={(panel && panel.lines) || []}/>
                        <PortalCard
                            data={panel && panel.portal}
                            loading={loading}
                            clientId={clientId}
                            onRefetch={refetchPanel}
                        />
                    </div>
                </div>
            </div>

            {/* Reactstrap no monta nada mientras está cerrado: vive acá afuera del `pnl-screen` para
                que el modal no herede el layout de la pantalla. */}
            <LineManageConfirmModal
                action={lineManage.confirmAction}
                onYes={lineManage.confirmYes}
                onNo={lineManage.cancel}
                isSaving={lineManage.escribiendo}
            />

            {showPathZero && (
                <NewLinePathModal
                    isOpen
                    toggle={() => setShowPathZero(false)}
                    clientId={clientId}
                    clientName={(panel && panel.client && panel.client.name) || ''}
                    mediaPlanAvailable={showMediaPlan}
                    onPickManual={openLineWizard}
                    onCreated={refetchPanel}
                />
            )}

            {/* `key` por línea (y otra al cerrar): cada apertura nace de cero, sin un render con el
                estado de la línea anterior ni un `saving` que sobreviva al cierre. */}
            <LineGoalModal
                key={goalLineId === null ? 'closed' : goalLineId}
                line={goalLine}
                isOpen={!!goalLine}
                onClose={closeGoalModal}
                onSaved={refetchPanel}
                onOpenFull={handleEditLine}
                clientId={clientId}
                economy={economy}
            />

            {/* Guardar los eventos de UNA cuenta puede cambiar el modo del cliente entero
                (ALL_PRIMARY → MIXED, por ejemplo): al éxito se refresca el agregado, no la lista. */}
            <ConversionModal
                isOpen={conversionOpen}
                toggle={closeConversion}
                conversion={panel && panel.monitored_conversion}
                adAccounts={panel && panel.platforms && panel.platforms.client_ad_accounts}
                onSaved={refetchPanel}
            />
        </section>
    );
}
